/**
 * sdhAPI_v2.ts — the v2 pages talk to the SAME backend as v1.
 *
 * Nothing here calls a new endpoint. Everything the redesign needs (sme_decision, version, revisions[]
 * with per-version snapshots, review_status) is already returned by the existing rules API, so v1 and v2
 * read identical data and any difference you see between the two pages is the DESIGN, not the data.
 *
 * This file exists only to (a) re-export what v2 uses so the imports stay tidy, and (b) add the small
 * derivations v2 needs — none of which require a backend change.
 */
import {
  ASSESSMENT_BY_VALUE, GATE_TONE, type EnrichedRule, type RuleVersion,
} from "../api/sdhAPI";

// re-export the v1 surface so v2 files import from one place
export * from "../api/sdhAPI";

/* ── the v2 status model ──────────────────────────────────────────────────
 * Four gates, exactly as v1. "Awaiting confirmation" is deliberately a FLAG, not a fifth gate — the rule
 * still carries her decision; it is the RULE that moved on, not her verdict. Keeping it a flag means no
 * new vocabulary to learn and no change to any count that already exists.
 */
export type V2Gate = "APPROVED" | "CONDITIONAL" | "PENDING" | "REJECTED" | "NOT_REVIEWED";

export interface V2Status {
  gate: V2Gate;
  label: string;
  tone: string;
  /** the rule changed after she reviewed it — her decision belongs to an older version */
  awaitingConfirmation: boolean;
  assessmentLabel?: string;
  comment?: string;
  by?: string;
  at?: string;
  reviewedVersion?: number;
}

const GATE_LABEL: Record<V2Gate, string> = {
  APPROVED: "Approved", CONDITIONAL: "Conditional", PENDING: "Pending",
  REJECTED: "Rejected", NOT_REVIEWED: "Not reviewed",
};
const TONE: Record<V2Gate, string> = { ...GATE_TONE, NOT_REVIEWED: "#8a97a3" } as any;

export function v2Status(rule: any): V2Status {
  const dec = rule?.sme_decision || {};
  const raw = String(dec.decision_gate || "").toUpperCase();
  const legacy = String(rule?.approval_status || "");
  const gate: V2Gate =
    (["APPROVED", "CONDITIONAL", "PENDING", "REJECTED"].includes(raw) ? raw as V2Gate
      : legacy === "AUTO_APPROVED" || legacy === "SME_APPROVED" ? "APPROVED"
      : legacy === "REJECTED" ? "REJECTED"
      : "NOT_REVIEWED");
  return {
    gate,
    label: GATE_LABEL[gate],
    tone: TONE[gate],
    // review_status is computed server-side by comparing rule.version to sme_decision.reviewed_version
    awaitingConfirmation: rule?.review_status === "needs_re_review",
    assessmentLabel: dec.assessment ? (ASSESSMENT_BY_VALUE[dec.assessment]?.label || dec.assessment) : undefined,
    comment: dec.comment || undefined,
    by: dec.sme_name || dec.sme_id || undefined,
    at: dec.decided_at ? String(dec.decided_at).replace("T", " ").slice(0, 16) : undefined,
    reviewedVersion: dec.reviewed_version,
  };
}

/* ── name continuity ──────────────────────────────────────────────────────
 * The displayed title is DERIVED from family + owner field, so a revision that changes the family also
 * renames the rule. We let the name update (it should describe what the rule now enforces) and instead
 * show a "was …" breadcrumb until she confirms, so the rule is still recognisable.
 * The previous title is reconstructed from the version snapshot — no backend change needed.
 */
const FAMILY_PHRASE: Record<string, (f: string) => string> = {
  code_domain: (f) => `${f} must be a valid governed value`,
  lookup: (f) => `${f} must be a valid governed value`,
  mandatory: (f) => `${f} must be provided`,
  conditional_mandatory: (f) => `${f} must be provided in scope`,
  uniqueness: (f) => `${f} must be unique`,
  range: (f) => `${f} must be within range`,
  referential: (f) => `${f} must reference an existing record`,
};

export function previousTitle(rule: any): string | undefined {
  const revs: any[] = rule?.revisions || [];
  if (revs.length < 2) return undefined;
  const cur = Number(rule?.version || 0);
  const prior = revs.filter((r) => Number(r.version) < cur).sort((a, b) => b.version - a.version)[0];
  const snap = prior?.snapshot;
  if (!snap) return undefined;
  if (snap.constraint_name) return String(snap.constraint_name);
  const fam = String(snap.constraint_family || "");
  const leaf = String(rule?.owner_field || rule?._raw?.owner_field_id || "").split("::").pop() || "";
  const phrase = FAMILY_PHRASE[fam];
  return phrase ? phrase(leaf) : undefined;
}

/** True when the current title differs from the title at the version she reviewed. */
export function titleChanged(rule: any): boolean {
  const prev = previousTitle(rule);
  return !!prev && !!rule?.rule_name && prev !== rule.rule_name;
}

/* ── the accept prompt ────────────────────────────────────────────────────
 * A CONDITIONAL_* category already states WHAT had to happen, so when the agent revises a rule we can
 * ask one question with the likely answer pre-selected instead of making her re-derive her own verdict.
 */
export function conditionOf(assessment?: string): string | undefined {
  return assessment ? ASSESSMENT_BY_VALUE[assessment]?.condition : undefined;
}

export function acceptPrompt(rule: any): { question: string; suggest: "APPROVED_AS_IS" | "KEEP_CONDITIONAL" } | null {
  const st = v2Status(rule);
  if (!st.awaitingConfirmation) return null;
  const cond = conditionOf((rule?.sme_decision || {}).assessment);
  if (st.gate === "CONDITIONAL" && cond) {
    return {
      question: `You marked this “${st.assessmentLabel}”. The agent has revised it. Is that condition now met (${cond})?`,
      suggest: "APPROVED_AS_IS",
    };
  }
  return {
    question: `This rule changed after your review${st.assessmentLabel ? ` (“${st.assessmentLabel}”)` : ""}. Does your decision still stand?`,
    suggest: "KEEP_CONDITIONAL",
  };
}

export type { EnrichedRule, RuleVersion };
